import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import { useState, useEffect } from "react";
import { api } from "@/config/api-fetch";
import { Section } from "@/components/section";
import { Button } from "@/components/button";
import { Spinner } from "@wordpress/components";
import { toast } from "react-toastify";
import { useCronStatus } from "@/hooks/use-cron-status";
import { formatDistanceToNowStrict } from "date-fns";

/**
 * Format the last check timestamp as "X ago" string.
 */
function formatLastCheck(lastCheck: number | null): string {
	if (!lastCheck) return __("Never", "antimanual");
	
	const date = new Date(lastCheck * 1000); // Convert Unix timestamp to JS Date
	return formatDistanceToNowStrict(date, { addSuffix: true });
}

export function CronStatus() {
	const {
		data: status,
		isLoading: isStatusLoading,
		error: statusError,
		refetch: refetchStatus,
		isRefetching: isStatusRefetching,
	} = useCronStatus();

	const [triggering, setTriggering] = useState(false);
	const [lastCheckDisplay, setLastCheckDisplay] = useState<string>("");

	// Update "last checked" display every second for real-time countdown
	useEffect(() => {
		if (!status?.last_check) {
			setLastCheckDisplay(__("Never", "antimanual"));
			return;
		}

		const updateDisplay = () => {
			setLastCheckDisplay(formatLastCheck(status.last_check));
		};

		updateDisplay();
		const interval = setInterval(updateDisplay, 1000);
		return () => clearInterval(interval);
	}, [status?.last_check]);

	const handleManualTrigger = async () => {
		setTriggering(true);
		try {
			await api.post("/auto-posting/manual-trigger", {});
			toast.success(__("Auto-posting check executed successfully!", "antimanual"));
			await refetchStatus();
		} catch {
			toast.error(__("Failed to trigger auto-posting check", "antimanual"));
		} finally {
			setTriggering(false);
		}
	};

	if (isStatusLoading && !status) {
		return (
			<Section className={css("cron-health-compact bg-light mb-6")}>
				<div className={css("flex justify-center items-center p-4")}>
					<Spinner className={css("w-5 h-5")} />
				</div>
			</Section>
		);
	}

	if (statusError) {
		return (
			<Section className={css("cron-health-compact bg-light mb-6")}>
				<div className={css("flex items-center justify-between")}>
					<div className={css("flex items-center gap-3")}>
						<h3 className={css("m-0 text-base")}>{__("Auto Posting Status", "antimanual")}</h3>
						<span className={css("text-red-500 text-sm font-semibold flex items-center gap-1")}>
							<span className={css("indicator unhealthy text-lg")}>●</span> {__("Error", "antimanual")}
						</span>
					</div>
					<Button small onClick={() => refetchStatus()}>
						{__("Retry", "antimanual")}
					</Button>
				</div>
			</Section>
		);
	}

	if (!status) return null;

	const queueStats = status.queue_stats || { total: 0, pending: 0, processing: 0, completed: 0, failed: 0 };

	return (
		<Section className={css("cron-health-compact bg-light mb-6")}>
			<div className={css("flex items-center justify-between flex-wrap gap-4")}>
				{/* Left: Title + Health Status + Last Checked */}
				<div className={css("flex items-center gap-4")}>
					<div className={css("flex items-center gap-2")}>
						<h3 className={css("m-0 text-base")}>{__("Auto Posting Status", "antimanual")}</h3>
						{status.is_healthy ? (
							<span className={css("text-green-500 text-sm font-semibold flex items-center gap-1")}>
								<span className={css("indicator healthy text-lg")}>●</span>
							</span>
						) : (
							<span className={css("text-orange-500 text-sm font-semibold flex items-center gap-1")}>
								<span className={css("indicator unhealthy text-lg")}>●</span>
							</span>
						)}
					</div>
					<div className={css("text-gray text-sm")}>
						<span>{__("Last Checked:", "antimanual")}</span>{" "}
						<span className={css("font-semibold", status.is_running ? "text-blue-500" : "")}>
							{status.is_running ? __("Running now...", "antimanual") : lastCheckDisplay}
						</span>
					</div>
				</div>

				{/* Center: Queue Stats (Pending & Processing only) */}
				<div className={css("flex items-center gap-6")}>
					<div className={css("flex items-center gap-2")}>
						<span className={css("text-gray text-sm")}>{__("Pending:", "antimanual")}</span>
						<span className={css("font-semibold text-sm", queueStats.pending > 0 ? "text-orange-500" : "text-gray-700")}>
							{queueStats.pending || 0}
						</span>
					</div>
					<div className={css("flex items-center gap-2")}>
						<span className={css("text-gray text-sm")}>{__("Processing:", "antimanual")}</span>
						<span className={css("font-semibold text-sm", queueStats.processing > 0 ? "text-blue-500" : "text-gray-700")}>
							{queueStats.processing || 0}
						</span>
					</div>
				</div>

				{/* Right: Action Buttons */}
				<div className={css("flex gap-2")}>
					<Button small onClick={handleManualTrigger} loading={triggering}>
						{__("Run Now", "antimanual")}
					</Button>
					<Button small loading={isStatusRefetching} onClick={() => refetchStatus()}>
						{__("Refresh", "antimanual")}
					</Button>
				</div>
			</div>
		</Section>
	);
}

